import { Component, DestroyRef, inject } from '@angular/core';
import { NavigationCancel, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { IonApp, IonRouterOutlet } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import {
  arrowDown,
  arrowBackOutline,
  arrowForwardOutline,
  arrowRedo,
  addOutline,
  alertCircleOutline,
  closeOutline,
  hourglassOutline,
  imageOutline,
  gridOutline,
  chevronUpOutline,
  chevronDownOutline,
  add,
  person,
  pencil,
  clipboard,
  cog,
  close,
  personAdd,
  home,
  grid,
  peopleCircle,
  ellipseOutline,
  ellipse,
  expandOutline,
  eyeOutline,
  trashBin,
  copy,
  chevronBack,
  chevronForward,
  people,
  earth,
  heart,
  heartOutline,
  checkmarkCircleOutline,
  closeCircleOutline,
  body,
  dice,
  remove,
  list,
  chatbubbles,
  chatbubblesOutline,
  checkmarkCircle,
  bookmark,
  bookmarkOutline,
  book,
  bookOutline,
  personCircle,
  personCircleOutline,
  peopleCircleOutline,
  sparklesOutline,
  flashOutline,
  peopleOutline,
  settingsOutline,
  send,
  checkmarkOutline,
  chevronForwardOutline,
  diceOutline,
  documentTextOutline,
  globeOutline,
  helpCircleOutline,
  homeOutline,
  informationCircleOutline,
  languageOutline,
  listOutline,
  logoGoogle,
  mapOutline,
  notificationsOutline,
  personAddOutline,
  personOutline,
  phonePortraitOutline,
  refreshOutline,
  searchOutline,
  shieldOutline,
  sparkles,
  statsChartOutline,
  storefrontOutline,
  trashBinOutline,
} from 'ionicons/icons';
import { WebPushRegistrationService } from './features/pwa/web-push-registration.service';
import { I18nService } from './features/i18n/i18n.service';
import { AppForegroundService } from './features/app-lifecycle/app-foreground.service';
import { AppUpdateService } from './features/app-lifecycle/app-update.service';
import { AppUpdateModalComponent } from './features/app-lifecycle/app-update-modal.component';
import { PwaInstallModalComponent } from './features/pwa/pwa-install-modal.component';
import { UsePwaService } from './features/pwa/pwa-service';
import { PosthogService } from './features/analytics/posthog.service';
import { CookieBannerComponent } from './features/common/components/cookie-banner/cookie-banner.component';
import { clearStuckPageTransitionStyles, NAV_TRANSITION_CLEANUP_DELAY_MS } from './utils/responsive-nav-animation';
import { BackNavigationService } from './features/common/services/back-navigation.service';
addIcons({
  arrowDown,
  arrowBackOutline,
  arrowForwardOutline,
  arrowRedo,
  addOutline,
  alertCircleOutline,
  closeOutline,
  hourglassOutline,
  imageOutline,
  gridOutline,
  grid,
  chevronUpOutline,
  chevronDownOutline,
  add,
  person,
  pencil,
  clipboard,
  cog,
  close,
  personAdd,
  home,
  peopleCircle,
  ellipseOutline,
  ellipse,
  expandOutline,
  eyeOutline,
  trashBin,
  copy,
  chevronBack,
  chevronForward,
  people,
  earth,
  heart,
  heartOutline,
  checkmarkCircleOutline,
  closeCircleOutline,
  body,
  dice,
  remove,
  list,
  chatbubbles,
  chatbubblesOutline,
  checkmarkCircle,
  book,
  bookOutline,
  bookmark,
  bookmarkOutline,
  personCircleOutline,
  peopleCircleOutline,
  sparklesOutline,
  flashOutline,
  peopleOutline,
  settingsOutline,
  send,
  checkmarkOutline,
  chevronForwardOutline,
  diceOutline,
  documentTextOutline,
  globeOutline,
  helpCircleOutline,
  homeOutline,
  informationCircleOutline,
  languageOutline,
  listOutline,
  logoGoogle,
  mapOutline,
  notificationsOutline,
  personAddOutline,
  personOutline,
  phonePortraitOutline,
  refreshOutline,
  searchOutline,
  shieldOutline,
  sparkles,
  statsChartOutline,
  storefrontOutline,
  trashBinOutline,
});
@Component({
  selector: 're-root',
  templateUrl: 'app.component.html',
  imports: [
    AppUpdateModalComponent,
    PwaInstallModalComponent,
    CookieBannerComponent,
    IonApp,
    IonRouterOutlet,
  ],
})
export class AppComponent {
  /** Side effect: registers Web Push when logged in (PWA / HTTPS production). */
  private readonly _webPushRegistration = inject(WebPushRegistrationService);
  private readonly _i18n = inject(I18nService);
  /** Side effect: foreground sync after long background pause. */
  private readonly _foreground = inject(AppForegroundService);
  /** Side effect: blocking update modal when a new service worker version is ready. */
  private readonly _appUpdate = inject(AppUpdateService);
  /** Side effect: PWA install prompts and engagement tracking. */
  private readonly _pwa = inject(UsePwaService);
  /** Side effect: PostHog analytics (gated by cookie consent). */
  private readonly _posthog = inject(PosthogService);
  private readonly _router = inject(Router);
  /** Global capture keeps all Ionic back buttons on the same route policy. */
  private readonly _backNavigation = inject(BackNavigationService);
  private readonly _destroyRef = inject(DestroyRef);
  private _cleanupTimer?: ReturnType<typeof setTimeout>;

  constructor() {
    this._i18n.init();

    const backButtonHandler = (event: Event) => {
      const backButton = event.composedPath().find(
        (target): target is HTMLElement =>
          target instanceof HTMLElement && target.tagName === 'ION-BACK-BUTTON',
      );
      if (!backButton) return;

      const fallback =
        (backButton as HTMLElement & { defaultHref?: string }).defaultHref ||
        backButton.getAttribute('default-href') ||
        undefined;
      this._backNavigation.goBack(event, fallback || undefined);
    };
    document.addEventListener('click', backButtonHandler, true);

    const navSub = this._router.events
      .pipe(
        filter(
          (event) =>
            event instanceof NavigationEnd || event instanceof NavigationCancel,
        ),
      )
      .subscribe((event) => {
        if (this._cleanupTimer) {
          clearTimeout(this._cleanupTimer);
        }

        const delay =
          event instanceof NavigationCancel ? 0 : NAV_TRANSITION_CLEANUP_DELAY_MS;

        this._cleanupTimer = setTimeout(() => {
          clearStuckPageTransitionStyles();
          this._cleanupTimer = undefined;
        }, delay);
      });

    this._destroyRef.onDestroy(() => {
      navSub.unsubscribe();
      if (this._cleanupTimer) {
        clearTimeout(this._cleanupTimer);
      }
      document.removeEventListener('click', backButtonHandler, true);
    });
  }
}
